import React, { useState, useRef, useEffect } from "react";
import "./FilterBy.css";

interface Option {
  id: number | string; // убрали null — все опции имеют ID
  name: string;
}

interface FilterByProps<T> {
  options: Option[];
  value: T | null;
  onChange: (value: T | null) => void;
  placeholder?: string;
  label?: string;
}

export const FilterBy = <T extends number | string>({
  options,
  value,
  onChange,
  placeholder = "Выберите...",
  label,
}: FilterByProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [userInput, setUserInput] = useState(""); // отдельное состояние для ввода пользователя
  const containerRef = useRef<HTMLDivElement>(null);

  // Синхронизация: если выбрано значение → показываем имя в инпуте
  useEffect(() => {
    if (value === null) {
      setSearchTerm("");
      setUserInput("");
    } else {
      const selected = options.find((opt) => opt.id === value);
      setSearchTerm(selected ? selected.name : "");
      setUserInput(""); // сбрасываем пользовательский ввод при выборе
    }
  }, [value, options]);

  // Закрытие по клику вне
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Добавляем опцию "Все" в начало списка
  const allOptions = [{ id: "all" as const, name: "Все" }, ...options];

  // Фильтрация по пользовательскому вводу
  const filteredOptions = userInput.trim() === ""
    ? allOptions
    : allOptions.filter((opt) =>
        opt.name.toLowerCase().includes(userInput.toLowerCase()),
      );

  const handleSelect = (id: T | "all") => {
    if (id === "all") {
      onChange(null);
      setSearchTerm("");
      setUserInput("");
    } else {
      onChange(id);
    }
    setIsOpen(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setUserInput(val);
    setSearchTerm(val); // обновляем отображение
    setIsOpen(true);

    // Если поле очищено — отключаем фильтр
    if (val.trim() === "") {
      onChange(null);
    }
  };

  return (
    <div className="filter-by-container" ref={containerRef}>
      {/* {label && <label className="filter-by-label">{label}</label>} */}
      <input
        type="text"
        className="filter-by-input"
        placeholder={placeholder}
        value={searchTerm}
        onChange={handleChange}
        onClick={() => {
          setUserInput(""); // сбрасываем пользовательский ввод для показа полного списка
          setIsOpen(true);
        }}
        autoComplete="off"
      />
      {isOpen && (
        <ul className="filter-by-options">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((opt) => (
              <li
                key={opt.id.toString()}
                onClick={() => handleSelect(opt.id as T | "all")}
                className={
                  (opt.id === "all" && value === null) || opt.id === value
                    ? "selected"
                    : ""
                }
              >
                {opt.name}
              </li>
            ))
          ) : (
            <li className="no-results">Ничего не найдено</li>
          )}
        </ul>
      )}
    </div>
  );
};
